import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { api } from '../../lib/api';
import { setToken, roleToRedirectPath } from '../../lib/auth';
import { useAuth } from '../../context/AuthContext';

type DemoRole =
  | 'student'
  | 'student_under16'
  | 'parent'
  | 'teacher'
  | 'secretariat'
  | 'school_admin';

type DemoSessionResponse = {
  token: string;
  role: string;
  full_name: string;
  is_platform_admin?: boolean;
  school_id?: number | null;
};

const roles: Array<{
  key: DemoRole;
  title: string;
  subtitle: string;
  icon: string;
}> = [
  {
    key: 'student',
    title: 'Elev (16+)',
    subtitle: 'Vezi panoul elevului (B2B).',
    icon: 'GraduationCap',
  },
  {
    key: 'student_under16',
    title: 'Elev (<16)',
    subtitle: 'Testează fluxul elevului minor.',
    icon: 'User',
  },
  {
    key: 'parent',
    title: 'Părinte',
    subtitle: 'Vezi panoul părintelui (copil minor).',
    icon: 'Users',
  },
  {
    key: 'teacher',
    title: 'Profesor',
    subtitle: 'Catalog, teme, comunicare.',
    icon: 'BookOpen',
  },
  {
    key: 'secretariat',
    title: 'Secretariat',
    subtitle: 'Dashboard secretariat (B2B).',
    icon: 'FolderOpen',
  },
  {
    key: 'school_admin',
    title: 'Admin școală',
    subtitle: 'Setări școală / management.',
    icon: 'Shield',
  },
];

const getApiErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: string } | undefined)?.detail;
    return detail || error.message || 'Eroare de rețea';
  }
  return error instanceof Error ? error.message : 'Eroare necunoscută';
};

export default function TestPortalPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { refresh } = useAuth();

  const externalDemoPortalUrl = useMemo(() => {
    const raw = String((import.meta as any)?.env?.VITE_DEMO_PORTAL_URL || '').trim();
    const candidate = raw || (() => {
      const host = String(window.location.hostname || '').toLowerCase();
      if (host === 'scola.ro' || host === 'www.scola.ro') return 'https://demo.scola.ro/test';
      return '';
    })();
    return candidate && (candidate.startsWith('http://') || candidate.startsWith('https://')) ? candidate : '';
  }, []);

  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [code, setCode] = useState<string>(() => String(searchParams.get('code') || ''));
  const [selectedRole, setSelectedRole] = useState<DemoRole>('student');
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [requestOpen, setRequestOpen] = useState(false);
  const [requestBusy, setRequestBusy] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const [requestSuccessId, setRequestSuccessId] = useState<number | null>(null);
  const [reqFirstName, setReqFirstName] = useState('');
  const [reqLastName, setReqLastName] = useState('');
  const [reqSchool, setReqSchool] = useState('');
  const [reqPhone, setReqPhone] = useState('');
  const [reqEmail, setReqEmail] = useState('');
  const [reqSubject, setReqSubject] = useState('Solicitare cod demo');
  const [reqRole, setReqRole] = useState<string>('Profesor');
  const [reqStudents, setReqStudents] = useState('');
  const [reqMessage, setReqMessage] = useState('');
  const [reqPrivacyOk, setReqPrivacyOk] = useState(false);

  useEffect(() => {
    let disposed = false;
    api
      .get<{ enabled: boolean }>('/api/demo/status')
      .then((resp) => {
        if (disposed) return;
        setEnabled(Boolean(resp.data?.enabled));
      })
      .catch(() => {
        if (disposed) return;
        setEnabled(false);
      });
    return () => {
      disposed = true;
    };
  }, []);

  // If the demo portal is disabled on this environment, but we have an external demo URL configured
  // (e.g. demo.scola.ro/test), redirect there automatically to avoid "grey button" confusion.
  useEffect(() => {
    if (enabled !== false) return;
    if (!externalDemoPortalUrl) return;
    try {
      const target = new URL(externalDemoPortalUrl);
      if (target.origin === window.location.origin) return;
      // Preserve incoming query params (e.g. ?code=...).
      for (const [k, v] of searchParams.entries()) {
        if (!target.searchParams.has(k)) target.searchParams.set(k, v);
      }
      window.location.replace(target.toString());
    } catch {
      // ignore invalid URL
    }
  }, [enabled, externalDemoPortalUrl, searchParams]);

  const canStart = useMemo(() => Boolean(code.trim()) && !isBusy, [code, isBusy]);

  const start = useCallback(async () => {
    setError(null);
    if (!code.trim()) {
      setError('Introdu codul de acces din email.');
      return;
    }
    setIsBusy(true);
    try {
      const resp = await api.post<DemoSessionResponse>('/api/demo/session', {
        code: code.trim(),
        role: selectedRole,
      });
      const token = String(resp.data?.token || '');
      if (!token) {
        throw new Error('Serverul nu a returnat token.');
      }
      setToken(token, true);
      await refresh();
      const target = roleToRedirectPath(resp.data.role as any);
      navigate(target, { replace: true });
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setIsBusy(false);
    }
  }, [code, navigate, refresh, selectedRole]);

  const submitRequest = useCallback(async () => {
    setRequestError(null);
    if (!reqPrivacyOk) {
      setRequestError('Te rugăm să confirmi că ești de acord cu prelucrarea datelor pentru a te contacta.');
      return;
    }
    if (!reqFirstName.trim() || !reqLastName.trim() || !reqSchool.trim() || !reqPhone.trim() || !reqEmail.trim()) {
      setRequestError('Completează nume, prenume, școala, telefon și email.');
      return;
    }
    setRequestBusy(true);
    try {
      const resp = await api.post<{ ok: boolean; id?: number; detail?: string }>('/api/demo/request-code', {
        first_name: reqFirstName.trim(),
        last_name: reqLastName.trim(),
        school: reqSchool.trim(),
        phone: reqPhone.trim(),
        email: reqEmail.trim(),
        subject: reqSubject.trim() || 'Solicitare cod demo',
        role: reqRole.trim(),
        estimated_students: reqStudents.trim(),
        message: reqMessage.trim(),
      });
      if (!resp.data?.ok) {
        throw new Error(String(resp.data?.detail || 'Nu am putut trimite solicitarea.'));
      }
      setRequestSuccessId(Number(resp.data?.id || 0) || null);
    } catch (e) {
      setRequestError(getApiErrorMessage(e));
    } finally {
      setRequestBusy(false);
    }
  }, [
    reqEmail,
    reqFirstName,
    reqLastName,
    reqMessage,
    reqPhone,
    reqPrivacyOk,
    reqRole,
    reqSchool,
    reqStudents,
    reqSubject,
  ]);

  return (
    <div className="min-h-screen bg-background">
      <div className="pt-16" />
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="flex items-start gap-3">
          <div className="mt-1 text-primary">
            <Icon name="FlaskConical" size={22} />
          </div>
          <div className="flex-1">
            <h1 className="text-2xl font-semibold text-foreground">Pagina de test (demo)</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Alege un rol și intră într-un cont demo temporar. Link-ul funcționează doar cu un cod primit pe email.
            </p>
          </div>
        </div>

        {enabled === false ? (
          <div className="mt-6 rounded-lg border border-border bg-card p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={18} className="mt-0.5 text-muted-foreground" />
              <div className="text-sm text-muted-foreground">
                Demo portal este dezactivat pe acest mediu.
                {externalDemoPortalUrl ? (
                  <div className="mt-2">
                    <a
                      href={externalDemoPortalUrl}
                      className="text-primary underline underline-offset-4"
                      rel="noreferrer"
                    >
                      Deschide portalul demo
                    </a>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="order-2 lg:order-1 lg:col-span-1">
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="text-sm font-medium text-foreground">Cod acces</div>
              <div className="mt-2">
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Cod din email"
                  autoComplete="one-time-code"
                />
              </div>
              {error ? <div className="mt-2 text-sm text-destructive">{error}</div> : null}
              <div className="mt-4">
                <Button
                  onClick={start}
                  disabled={!canStart || enabled === false}
                  className="w-full"
                  iconName="LogIn"
                >
                  {isBusy ? 'Se autentifică...' : 'Intră în demo'}
                </Button>
              </div>
              <div className="mt-3 text-xs text-muted-foreground">
                Sesiune temporară (JWT) — ideală pentru testare rapidă fără parole.
              </div>
              <div className="mt-4 pt-4 border-t border-border">
                <button
                  type="button"
                  className="text-sm text-primary underline underline-offset-4"
                  onClick={() => {
                    setRequestOpen(true);
                    setRequestSuccessId(null);
                    setRequestError(null);
                  }}
                >
                  Nu ai cod? Solicită cod
                </button>
                <div className="mt-1 text-xs text-muted-foreground">
                  Completezi un formular scurt, iar administratorul platformei te contactează.
                </div>
              </div>
            </div>
          </div>

          <div className="order-1 lg:order-2 lg:col-span-2">
            <div className="rounded-lg border border-border bg-card p-4">
              <div className="text-sm font-medium text-foreground">Alege rolul</div>
              <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {roles.map((r) => {
                  const active = r.key === selectedRole;
                  return (
                    <button
                      key={r.key}
                      type="button"
                      onClick={() => setSelectedRole(r.key)}
                      className={[
                        'text-left rounded-md border p-4 transition-colors duration-150',
                        active
                          ? 'border-primary bg-primary/5'
                          : 'border-border hover:bg-muted/60',
                      ].join(' ')}
                    >
                      <div className="flex items-start gap-3">
                        <div className={active ? 'text-primary' : 'text-muted-foreground'}>
                          <Icon name={r.icon as any} size={18} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-foreground">{r.title}</div>
                          <div className="mt-0.5 text-xs text-muted-foreground">{r.subtitle}</div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="mt-4 rounded-lg border border-border bg-card p-4">
              <div className="flex items-start gap-3">
                <Icon name="ShieldAlert" size={18} className="mt-0.5 text-muted-foreground" />
                <div className="text-sm text-muted-foreground">
                  Folosește conturi demo, fără date reale. Trimite link-ul din email doar persoanelor care trebuie să
                  testeze.
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {requestOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4"
          role="dialog"
          aria-modal="true"
          aria-label="Solicită cod demo"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setRequestOpen(false);
          }}
        >
          <div className="w-full max-w-xl max-h-[85vh] rounded-xl border border-border bg-card shadow-lg overflow-hidden">
            <div className="flex items-start justify-between gap-3 p-4 border-b border-border">
              <div className="flex items-start gap-2">
                <Icon name="Mail" size={18} className="text-muted-foreground mt-0.5" />
                <div>
                  <div className="text-base font-semibold text-foreground">Solicită cod demo</div>
                  <div className="text-sm text-muted-foreground">
                    Trimitem solicitarea către administratorul platformei și te contactăm cât mai curând.
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="p-2 rounded-md hover:bg-muted"
                onClick={() => setRequestOpen(false)}
                aria-label="Închide"
              >
                <Icon name="X" size={18} className="text-muted-foreground" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[calc(85vh-72px)]">
              {requestSuccessId ? (
                <div className="space-y-2">
                  <div className="text-sm font-semibold text-foreground">Mulțumim!</div>
                  <div className="text-sm text-muted-foreground">
                    Solicitarea ta a fost înregistrată{requestSuccessId ? (
                      <>
                        {' '}
                        cu ID <span className="text-foreground">#{requestSuccessId}</span>
                      </>
                    ) : null}
                    . În cel mai scurt timp te vom contacta.
                  </div>
                  <div className="pt-3 flex justify-end gap-2">
                    <Button variant="default" onClick={() => setRequestOpen(false)}>
                      Închide
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input label="Nume" value={reqLastName} onChange={(e) => setReqLastName(e.target.value)} />
                    <Input label="Prenume" value={reqFirstName} onChange={(e) => setReqFirstName(e.target.value)} />
                  </div>
                  <Input label="Școala / instituția" value={reqSchool} onChange={(e) => setReqSchool(e.target.value)} />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Input label="Telefon" value={reqPhone} onChange={(e) => setReqPhone(e.target.value)} />
                    <Input label="Email" value={reqEmail} onChange={(e) => setReqEmail(e.target.value)} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <label className="space-y-1">
                      <div className="text-sm font-medium text-foreground">Rol pentru demo</div>
                      <select
                        className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
                        value={reqRole}
                        onChange={(e) => setReqRole(e.target.value)}
                      >
                        <option>Profesor</option>
                        <option>Admin școală</option>
                        <option>Secretariat</option>
                        <option>Părinte</option>
                        <option>Elev (16+)</option>
                        <option>Elev (&lt;16)</option>
                      </select>
                    </label>
                    <Input
                      label="Nr. elevi (estimativ, opțional)"
                      value={reqStudents}
                      onChange={(e) => setReqStudents(e.target.value)}
                    />
                  </div>
                  <Input label="Subiect" value={reqSubject} onChange={(e) => setReqSubject(e.target.value)} />
                  <label className="space-y-1 block">
                    <div className="text-sm font-medium text-foreground">Mesaj (opțional)</div>
                    <textarea
                      className="w-full min-h-20 rounded-md border border-border bg-background px-3 py-2 text-sm"
                      placeholder="Ex.: Ce vrei să testezi, când ai dori o prezentare, etc."
                      value={reqMessage}
                      onChange={(e) => setReqMessage(e.target.value)}
                    />
                  </label>

                  <label className="flex items-start gap-3 text-sm">
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={reqPrivacyOk}
                      onChange={(e) => setReqPrivacyOk(e.target.checked)}
                    />
                    <span className="text-muted-foreground">
                      Sunt de acord ca datele transmise să fie prelucrate pentru a fi contactat(ă) legat de solicitarea de demo.
                    </span>
                  </label>

                  {requestError ? <div className="text-sm text-destructive">{requestError}</div> : null}

                  <div className="pt-2 flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setRequestOpen(false)} disabled={requestBusy}>
                      Anulează
                    </Button>
                    <Button variant="default" onClick={() => void submitRequest()} disabled={requestBusy}>
                      {requestBusy ? 'Se trimite...' : 'Trimite solicitarea'}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
